import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Link2, Clock, Pencil, Trash2, Save, Loader2 } from 'lucide-react';

// Base reference interface - compatible with TTMLElement
interface Reference {
  id: string;
  start: number;
  end: number;
  type: string;
  content: string;
  title?: string;
  url?: string;
  linkText?: string;
  category?: string;
  context?: string;
  visualSuggestion?: string;
}

interface ReferenceEditModalProps<T extends Reference = Reference> {
  isOpen: boolean;
  onClose: () => void;
  reference: T | null;
  allReferences: T[];
  onSelectReference: (ref: T) => void;
  onSave: (reference: T, updates: ReferenceUpdates) => Promise<void>;
  onDelete: (reference: T) => Promise<void>;
  onSeek: (time: number) => void;
  isDarkMode: boolean;
  canEdit: boolean;
}

export interface ReferenceUpdates {
  name: string;
  category?: string;
  linkText?: string;
  url?: string;
  context?: string;
  visualSuggestion?: string;
}

// Helper to format time
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

function ReferenceEditModal<T extends Reference>({
  isOpen,
  onClose,
  reference,
  allReferences,
  onSelectReference,
  onSave,
  onDelete,
  onSeek,
  isDarkMode,
  canEdit
}: ReferenceEditModalProps<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editLinkText, setEditLinkText] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editContext, setEditContext] = useState('');
  const [editVisualSuggestion, setEditVisualSuggestion] = useState('');

  // Reset form when reference changes or modal opens
  useEffect(() => {
    if (reference) {
      setEditName(reference.title || '');
      setEditCategory(reference.category || '');
      setEditLinkText(reference.linkText || '');
      setEditUrl(reference.url || '');
      setEditContext(reference.context || '');
      setEditVisualSuggestion(reference.visualSuggestion || '');
    }
    setIsEditing(false);
    setShowDeleteConfirm(false);
    setError(null);
  }, [reference, isOpen]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancelEdit = () => {
    // Reset to original values
    if (reference) {
      setEditName(reference.title || '');
      setEditCategory(reference.category || '');
      setEditLinkText(reference.linkText || '');
      setEditUrl(reference.url || '');
      setEditContext(reference.context || '');
      setEditVisualSuggestion(reference.visualSuggestion || '');
    }
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!reference) return;

    if (!editName.trim()) {
      setError('Reference name is required');
      return;
    }

    // Validate URL if provided
    if (editUrl && !editUrl.startsWith('http://') && !editUrl.startsWith('https://')) {
      setError('URL must start with http:// or https://');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(reference, {
        name: editName.trim(),
        category: editCategory.trim() || undefined,
        linkText: editLinkText.trim() || undefined,
        url: editUrl.trim() || undefined,
        context: editContext.trim() || undefined,
        visualSuggestion: editVisualSuggestion.trim() || undefined
      });
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!reference) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onDelete(reference);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete reference');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isSaving && !isDeleting) {
      onClose();
    }
  };

  if (!reference) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#232E3F] to-[#FFB52B]">
                  <Link2 size={16} className="text-white" />
                </div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {isEditing ? 'Edit Reference' : 'Reference'}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {canEdit && !isEditing && !showDeleteConfirm && (
                  <>
                    <button
                      onClick={handleStartEdit}
                      className={`p-2 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-700 text-gray-400 hover:text-[#FFCF74]'
                          : 'hover:bg-gray-100 text-gray-500 hover:text-[#232E3F]'
                      }`}
                      title="Edit reference"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className={`p-2 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-700 text-gray-400 hover:text-red-400'
                          : 'hover:bg-gray-100 text-gray-500 hover:text-red-600'
                      }`}
                      title="Delete reference"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
                <button
                  onClick={handleClose}
                  disabled={isSaving || isDeleting}
                  className={`p-2 rounded-full transition-colors ${
                    isDarkMode
                      ? 'hover:bg-gray-700 text-gray-400'
                      : 'hover:bg-gray-100 text-gray-500'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="mx-5 mt-4 px-3 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
              <div className="px-5 py-4">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                    Are you sure you want to delete this reference? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          Delete Reference
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Content - View Mode */}
            {!isEditing && !showDeleteConfirm && (
              <div className="px-5 py-4 space-y-4">
                {/* Title */}
                <div>
                  <h4 className={`text-xl font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {reference.title}
                  </h4>
                </div>

                {/* Category */}
                {reference.category && (
                  <div className="flex items-center gap-2">
                    <span className={`text-sm px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-[#232E3F] text-[#FFE3A1]' : 'bg-[#EEF1F3] text-[#232E3F]'}`}>
                      {reference.category}
                    </span>
                  </div>
                )}

                {/* Context */}
                {reference.context && (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {reference.context}
                  </p>
                )}

                {/* URL Link */}
                {reference.url && (
                  <a
                    href={reference.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                      isDarkMode
                        ? 'bg-[#232E3F]/75 hover:bg-[#2F3B4E] text-[#FFE3A1]'
                        : 'bg-[#FFF8EA] hover:bg-[#FFE7AD] text-[#8B5B00]'
                    }`}
                  >
                    <ExternalLink size={18} />
                    <span className="flex-1 truncate">
                      {reference.linkText || reference.url}
                    </span>
                  </a>
                )}

                {/* Timestamp - clickable to seek */}
                <button
                  onClick={() => {
                    onSeek(reference.start);
                    onClose();
                  }}
                  className={`text-xs flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${
                    isDarkMode
                      ? 'text-[#FFCF74] hover:bg-[#232E3F]/40'
                      : 'text-[#B57700] hover:bg-[#FFF4D8]'
                  }`}
                >
                  <Clock size={12} />
                  {formatTime(reference.start)}
                </button>
              </div>
            )}

            {/* Content - Edit Mode */}
            {isEditing && !showDeleteConfirm && (
              <div className="px-5 py-4 space-y-4">
                {/* Name field - required */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Reference name..."
                    disabled={isSaving}
                    className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                      isDarkMode
                        ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Category and Link Text row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Category
                    </label>
                    <input
                      type="text"
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      placeholder="Person, Paper, Book..."
                      disabled={isSaving}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                        isDarkMode
                          ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Link Text
                    </label>
                    <input
                      type="text"
                      value={editLinkText}
                      onChange={(e) => setEditLinkText(e.target.value)}
                      placeholder="arXiv Paper, Website..."
                      disabled={isSaving}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                        isDarkMode
                          ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                </div>

                {/* URL field */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    URL
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      placeholder="https://..."
                      disabled={isSaving}
                      className={`flex-1 px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                        isDarkMode
                          ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                    {editUrl && editUrl.startsWith('http') && (
                      <a
                        href={editUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg p-2 text-[#B57700] transition-colors hover:bg-[#FFF4D8] hover:text-[#8B5B00] dark:text-[#FFCF74] dark:hover:bg-[#232E3F]/40"
                        title="Open URL"
                      >
                        <ExternalLink size={18} />
                      </a>
                    )}
                  </div>
                </div>

                {/* Context field */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Context
                  </label>
                  <textarea
                    value={editContext}
                    onChange={(e) => setEditContext(e.target.value)}
                    placeholder="Why this reference is relevant..."
                    rows={2}
                    disabled={isSaving}
                    className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] resize-y ${
                      isDarkMode
                        ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Visual Suggestion field */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Visual Suggestion
                  </label>
                  <input
                    type="text"
                    value={editVisualSuggestion}
                    onChange={(e) => setEditVisualSuggestion(e.target.value)}
                    placeholder="Photo, diagram, book cover..."
                    disabled={isSaving}
                    className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                      isDarkMode
                        ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Edit mode buttons */}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !editName.trim()}
                    className="px-4 py-2 rounded-lg font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'rgb(236, 72, 153)' }}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Footer with all references as table */}
            {allReferences.length > 1 && !isEditing && !showDeleteConfirm && (
              <div className={`px-5 py-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                <h5 className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  All References ({allReferences.length})
                </h5>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {allReferences.map((ref) => (
                        <tr
                          key={ref.id}
                          onClick={() => onSelectReference(ref)}
                          className={`cursor-pointer transition-colors ${
                            reference?.id === ref.id
                              ? isDarkMode ? 'bg-[#232E3F]/70' : 'bg-[#EEF1F3]'
                              : isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                          }`}
                        >
                          <td className="py-1.5 px-2 w-8">
                            {ref.url ? (
                              <Link2 size={12} className="text-[#FFCF74]" />
                            ) : (
                              <span className={`inline-block w-3 h-3 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                            )}
                          </td>
                          <td className={`py-1.5 px-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <span className="font-medium">{ref.title}</span>
                            {ref.category && (
                              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                                {ref.category}
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-right">
                            {ref.url && (
                              <a
                                href={ref.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-[#FFCF74] hover:text-[#FFE3A1] hover:underline"
                              >
                                {ref.linkText || 'Link'} ↗
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReferenceEditModal;
